"use client"
import { useState, useEffect } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { ResourcePostData, resourcesData } from './post-data';

interface ResourcePostProps {
    post: ResourcePostData;
}

const ResourcePost = ({ post }: ResourcePostProps) => {
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001
    });

    const [activeSection, setActiveSection] = useState(post.sections[0]?.id || "");

    useEffect(() => {
        const handleScroll = () => {
            const scrollPosition = window.scrollY + 200;
            for (const section of post.sections) {
                const element = document.getElementById(section.id);
                if (element && element.offsetTop <= scrollPosition) {
                    setActiveSection(section.id);
                }
            }
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [post.sections]);

    const scrollToSection = (id: string) => {
        const element = document.getElementById(id);
        if (element) {
            window.scrollTo({
                top: element.offsetTop - 100,
                behavior: 'smooth'
            });
        }
    };

    // Filter out the current post to get other "popular" reads
    const popularReads = resourcesData.filter(p => p.id !== post.id).slice(0, 5);

    return (
        <div className="min-h-screen bg-[#ffffff] font-jakarta">
            {/* Scroll Progress Bar */}
            <motion.div
                className="fixed top-0 left-0 right-0 h-1 bg-[#2563EB] origin-left z-[100]"
                style={{ scaleX }}
            />

            {/* Hero Section - Creative Canvas Style */}
            <header className="relative pt-32 pb-10 overflow-hidden bg-slate-900 min-h-[60vh] flex items-center justify-center">

                {/* 1. Background Gradient & Noise */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#0F172A] opacity-100" />

                {/* Noise Texture */}
                <div className="absolute inset-0 opacity-[0.2] mix-blend-overlay pointer-events-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
                />

                {/* 2. Abstract Shapes/Orbs */}
                <motion.div
                    className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] bg-blue-500/30 rounded-full blur-[120px] mix-blend-screen"
                    animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }}
                    transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-purple-500/20 rounded-full blur-[120px] mix-blend-screen"
                    animate={{ scale: [1, 1.2, 1], x: [0, -30, 0] }}
                    transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
                />

                {/* 3. Scribbles & Doodles */}
                <div className="absolute top-32 left-[10%] opacity-30 pointer-events-none hidden md:block">
                    <svg width="100" height="100" viewBox="0 0 100 100" fill="none" stroke="white" strokeWidth="2">
                        <motion.path
                            d="M10 10 Q 50 50, 10 90"
                            initial={{ pathLength: 0 }}
                            animate={{ pathLength: 1 }}
                            transition={{ duration: 2, delay: 0.5 }}
                        />
                    </svg>
                </div>
                <div className="absolute bottom-10 right-[15%] opacity-20 pointer-events-none">
                    <svg width="120" height="120" viewBox="0 0 120 120" fill="none">
                        <circle cx="60" cy="60" r="50" stroke="white" strokeWidth="2" strokeDasharray="10 10" opacity="0.5" />
                    </svg>
                </div>

                <div className="relative max-w-[1000px] mx-auto px-6 text-center z-10 text-white">
                    {/* Meta Tags */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex flex-wrap items-center justify-center gap-4 mb-8"
                    >
                        <span className="bg-white/10 backdrop-blur-md text-blue-200 px-4 py-1.5 rounded-full font-medium text-[12px] border border-white/10 shadow-lg">
                            {post.series || "Article"}
                        </span>
                        <span className="text-white/20">|</span>
                        <div className="flex items-center gap-6 text-blue-100/70 font-medium text-[13px]">
                            <span className="flex items-center gap-1.5">
                                <Calendar size={14} className="text-blue-400" />
                                {post.date}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Clock size={14} className="text-blue-400" />
                                {post.readTime}
                            </span>
                        </div>
                    </motion.div>

                    {/* Title */}
                    <motion.h1
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="font-syne font-bold text-4xl md:text-5xl lg:text-[64px] text-white leading-[1.1] mb-10 tracking-tight drop-shadow-2xl"
                    >
                        {post.title}
                    </motion.h1>


                </div>
            </header>

            {/* Main Content Layout */}
            <div className="max-w-[1440px] mx-auto px-6 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">

                    {/* LEFT SIDEBAR: Table of Contents */}
                    <aside className="hidden lg:block lg:col-span-2">
                        <div className="sticky top-32">
                            <h4 className="font-syne font-bold text-[16px] text-gray-400 mb-6">
                                Contents
                            </h4>
                            <nav className="relative border-l border-gray-200 ml-1">
                                {post.sections.map((section) => (
                                    <button
                                        key={section.id}
                                        onClick={() => scrollToSection(section.id)}
                                        className={`block w-full text-left pl-5 py-2.5 text-[13px] font-medium transition-all duration-300 border-l-2 -ml-[1px] ${activeSection === section.id
                                            ? "border-[#2563EB] text-[#2563EB]"
                                            : "border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300"
                                            }`}
                                    >
                                        {section.title}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </aside>

                    {/* CENTER: Main Content (Text Only, No UI/Images) */}
                    <main className="lg:col-span-7 font-jakarta">
                        {post.sections.map((section, idx) => (
                            <div key={section.id} id={section.id} className={`mb-14 ${idx !== 0 ? 'pt-10 border-t border-gray-100' : ''}`}>
                                <h2 className="font-jakarta font-extrabold text-[32px] text-gray-900 mb-6 leading-tight">
                                    {section.title}
                                </h2>
                                <div className="text-[16.5px] leading-[1.8] text-gray-600 space-y-6 lg:max-w-[85%]">
                                    {section.content}
                                </div>
                            </div>
                        ))}
                    </main>

                    {/* RIGHT SIDEBAR: Popular Blogs Only (No CTA) */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-32">
                            <h4 className="font-syne font-bold text-[16px] text-gray-400 mb-6">
                                Popular Reads
                            </h4>
                            <div className="space-y-6">
                                {popularReads.map((otherPost) => (
                                    <Link key={otherPost.id} href={`/resources/${otherPost.id}`} className="block group cursor-pointer border-b border-gray-50 pb-4 last:border-0 hover:pl-2 transition-all duration-300">
                                        <h5 className="text-[13px] font-semibold text-gray-700 leading-[1.4] group-hover:text-[#2563EB] transition-colors mb-2">
                                            {otherPost.title}
                                        </h5>
                                        <span className="text-[11px] text-gray-400 flex items-center gap-1 font-medium">
                                            <ArrowRight size={10} className="group-hover:translate-x-1 transition-transform" />
                                            Read Article
                                        </span>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default ResourcePost;
